import { useEffect } from "react";
import { useSelector } from "react-redux";

/**
 * ── 변경사항 (ECHO 톤) ──
 * 1. 체크박스 + 텍스트 4개 가로 나열 → 칩 버튼 (줄바꿈 깨짐 해결)
 * 2. MUI Switch (파란색) → ECHO 민트 토글
 * 3. select 박스 ECHO 톤 (라운드 10px, 보더 #E6EDEC)
 * 4. 소제목 폰트 통일 (0.72rem 800 letterSpacing)
 * 5. 로직·데이터 구조는 기존 그대로
 */
const SentenceTestSelection = ({
  allTypes,
  selectedTypes,
  setSelectedTypes,
}) => {
  const { worksheet } = useSelector((state) => state.worksheet);

  const maxExamCount =
    setSelectedTypes === undefined
      ? 99
      : worksheet.data[0].data
          .filter((item) => item.type === "sentence_test")[0]
          .data.filter((sectionData) => sectionData.type === "kor_blank")[0]
          .data.length;

  const examIndex = selectedTypes.find(
    (item) => item.key === "sentence_test"
  ).examIndex;

  const isDictation = selectedTypes.find(
    (item) => item.key === "sentence_test"
  ).is_dictation;

  const handleOptionClick = (option) => {
    setSelectedTypes((prev) =>
      prev.map((item) => {
        if (item.key === "sentence_test") {
          if (item.options.some((opt) => opt.key === option.key)) {
            return {
              ...item,
              options: item.options.filter((opt) => opt.key !== option.key),
            };
          }
          return { ...item, options: [...item.options, option] };
        }
        return item;
      })
    );
  };

  const handleExamIndexChange = (index) => {
    setSelectedTypes((prev) =>
      prev.map((item) =>
        item.key === "sentence_test" ? { ...item, examIndex: index } : item
      )
    );
  };

  const handleDictationModeChange = (value) => {
    setSelectedTypes((prev) =>
      prev.map((item) =>
        item.key === "sentence_test" ? { ...item, is_dictation: value } : item
      )
    );
  };

  useEffect(() => {
    handleDictationModeChange(true);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (setSelectedTypes !== undefined) {
      handleExamIndexChange(1);
    }
    // eslint-disable-next-line
  }, [maxExamCount]);

  const readOnly = setSelectedTypes === undefined;
  const currentOptions =
    selectedTypes.find((t) => t.key === "sentence_test")?.options || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
      {/* 유형 선택 — 칩 */}
      <div>
        <div style={labelStyle}>문장학습 유형</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {allTypes
            .find((type) => type.key === "sentence_test")
            .options.map((option) => {
              const checked = currentOptions.some(
                (item) => item.key === option.key
              );
              return (
                <button
                  key={option.key}
                  onClick={() => !readOnly && handleOptionClick(option)}
                  disabled={readOnly}
                  style={{
                    padding: "7px 14px",
                    border: `1.5px solid ${checked ? "#00D9C0" : "#E6EDEC"}`,
                    backgroundColor: checked ? "#E5FBF8" : "#FFF",
                    color: checked ? "#00A396" : "#7A8490",
                    borderRadius: "999px",
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    fontFamily: "inherit",
                    letterSpacing: "-0.01em",
                    cursor: readOnly ? "default" : "pointer",
                    whiteSpace: "nowrap",
                    transition: "all .12s",
                  }}
                >
                  {option.value}
                </button>
              );
            })}
        </div>
      </div>

      {/* 세트 선택 */}
      <div>
        <div style={labelStyle}>문장학습 세트</div>
        <select
          value={examIndex}
          onChange={(e) => handleExamIndexChange(e.target.value)}
          disabled={readOnly}
          className="customSelect"
          style={{
            padding: "9px 13px",
            fontSize: "0.86rem",
            color: "#0E141B",
            fontWeight: 600,
            width: "130px",
            border: "1.5px solid #E6EDEC",
            borderRadius: "10px",
            cursor: readOnly ? "default" : "pointer",
            backgroundColor: "#FFF",
            fontFamily: "inherit",
          }}
        >
          {[...Array(maxExamCount)].map((_, index) => (
            <option key={index + 1} value={index + 1}>
              {index + 1}번 세트
            </option>
          ))}
        </select>
      </div>

      {/* Dictation 토글 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "11px 14px",
          backgroundColor: "#F4F8F7",
          borderRadius: "12px",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: "0.86rem",
              fontWeight: 800,
              color: "#0E141B",
              letterSpacing: "-0.01em",
            }}
          >
            Dictation 모드
          </div>
          <div
            style={{ fontSize: "0.74rem", color: "#7A8490", marginTop: "1px" }}
          >
            문장을 듣고 받아쓰는 방식으로 출제됩니다
          </div>
        </div>
        <div
          onClick={() => !readOnly && handleDictationModeChange(!isDictation)}
          style={{
            width: "46px",
            height: "27px",
            borderRadius: "999px",
            backgroundColor: isDictation ? "#00D9C0" : "#D6DEE0",
            position: "relative",
            cursor: readOnly ? "default" : "pointer",
            transition: "background-color .15s",
            flexShrink: 0,
          }}
        >
          <span
            style={{
              position: "absolute",
              top: "3px",
              left: isDictation ? "22px" : "3px",
              width: "21px",
              height: "21px",
              borderRadius: "50%",
              backgroundColor: "#FFF",
              boxShadow: "0 1px 3px rgba(14,20,27,.18)",
              transition: "left .15s",
            }}
          />
        </div>
      </div>
    </div>
  );
};

const labelStyle = {
  fontSize: "0.72rem",
  fontWeight: 800,
  letterSpacing: "0.06em",
  color: "#7A8490",
  marginBottom: "7px",
};

export default SentenceTestSelection;
